import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Collapse, Dialog, DialogContent, Grid, Link, Typography, useTheme } from "@mui/material";
import Card from "../../common/Card";
import Table, { Row } from "../../common/CustomTable";
import Header from "../../common/Header";
import Button from "../../common/Button";
import { AutoAwesome, SearchOutlined, UploadFileOutlined, DriveFileRenameOutline } from '@mui/icons-material';
import { Brain } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { useModalStyles } from '../../common/useModalStyles';
import { tokens } from '../../../theme';
import CustomTextField from '../../common/CustomTextField';
import CustomDateRange from '../../common/CustomDateRange';
import CustomMultipleDropDown from '../../common/CustomMultipleDropDown';
import { DateObject } from 'react-multi-date-picker';
import CustomCommentPopup from '../../common/CustomCommentPopup';
import { useNavigation } from '../../../hooks/useNavigation';
import { useFilterPanelFocus } from "../../../hooks/useFilterPanelFocus";
import MedicalNoteAuditPopup from './MedicalNoteAuditPopup';
import MedicalNoteAuditUploadPopup from './MedicalNoteAuditUploadPopup';
import AiAuditAgreement from './AiAuditAgreement';
import { useAppDispatch } from '../../../redux/slice/redux-hooks';
import { getPracticeListforFilter, usePracticeSlice } from '../../../redux/slice/practiceSlice';
import { AiAuditInfo, fetchAiAuditList, fetchAiServiceAgreementStatus, getRecentAiAuditDetailsByIdData, resetAiAuditList, resetServiceAgreementStatus, SearchAiAuditInfo, useAiAuditReducer } from '../../../redux/slice/AI/AiAuditSlice';
import { getComplianceColor } from '../../../utils/Utility';
import { createListSort } from '../../../utils/listSort';
import { saveSelectionForPage } from '../../../utils/StorageUtils';
import { useSelector } from 'react-redux';
import { selectIsValueFoundInToken, useAuthSlice } from '../../../redux/slice/authSlice';
import { AI_SERVICE_NAME, CAN_ACCESS_APPLICATION, USER_ROLES } from '../../../constants/applicationConstants';
import { RootState } from '../../../redux/store';
import { fetchUsersByUserInput, useUserReducer } from '../../../redux/slice/userSlice';
import { setSingleAlertObj, SingleAlertInfo } from "../../../redux/slice/commonSlice";

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "center" | "left";
  format?: (value: number) => string;
}

interface SearchAiAudit {
  patientName: string;
  practiceIds: number[];
  fromDate: string;
  toDate: string;
  dateRange?: string;
  currentPage: number;
  itemsPerPage: number;
  // CS-1706 - Uploaded By filter (admin only)
  uploadedByUserIds?: number[];
  serviceNames?: string[];
  sortBy?: SearchAiAuditInfo["sortBy"];
  sortOrder?: SearchAiAuditInfo["sortOrder"];
}

const ITEMS_PER_PAGE = 10;

const aiAuditListSort = createListSort(
  ["auditId", "patientName", "practiceName", "fileName", "createdByUserName", "createdAt", "currentCompliancePercentage"] as const,
  {
    auditIdLink: "auditId",
    patientName: "patientName",
    practiceName: "practiceName",
    fileName: "fileName",
    createdByUserName: "createdByUserName",
    createdAt: "createdAt",
    currentCompliancePercentage: "currentCompliancePercentage",
  }
);

const AiAuditList: React.FC = () => {
  const location = useLocation();
  const { navigate } = useNavigation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const { primaryBtnSx, tokens: modalTokens } = useModalStyles();

  const { control, handleSubmit, setValue, getValues } = useForm<SearchAiAudit>();
  const [showCard, setShowCard] = useState(true);
  const {
    filterPanelRef,
    toggleFilterPanel,
    handleFilterPanelEntering,
    handleFilterPanelEntered,
    handleFilterPanelExited,
  } = useFilterPanelFocus(showCard, setShowCard);
  const [showAuditPopup, setShowAuditPopup] = useState(false);
  const [showUploadPopup, setShowUploadPopup] = useState(false);
  const [selectedAuditData, setSelectedAuditData] = useState<any>(null);
  const [showCommentPopup, setShowCommentPopup] = useState(false);
  const [commentData, setCommentData] = useState({ title: "", body: "" });
  const [isDateRangeChanged, setIsDateRangeChanged] = useState(false);
  const [isClearDateRange, setIsClearDateRange] = useState(false);
  // CS-1706 START - State for showing agreement info popup and agreement dialog
  const [showAgreementInfoPopup, setShowAgreementInfoPopup] = useState(false);
  const [showAgreementDialog, setShowAgreementDialog] = useState(false);
  // CS-1706 END
  // CS-1706 - Start - Store active button selection
  const [activeButtonId, setActiveButtonId] = useState<string | null>(null);
  // CS-1706 - End

  const { practiceByUserRole } = usePracticeSlice();
  const { aiAuditList, serviceAgreementStatus } = useAiAuditReducer();
  const hasSubscribedAccessToMedicalAudit = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessApplication", CAN_ACCESS_APPLICATION.MEDICAL_NOTE_AUDIT)(state));
  const { userInfo } = useAuthSlice();
  // CS-1706 - Start - Fetch user list for "Uploaded By" filter (admin only)
  const { fetchUsersByQuery } = useUserReducer();
  const isAdminUser = userInfo.userRole === USER_ROLES.ROLE_ADMIN || userInfo.userRole === USER_ROLES.ROLE_SUPERADMIN;
  // CS-1706 - End
  const [searchCriteria, setSearchCriteria] = useState<SearchAiAudit>({
    patientName: "",
    practiceIds: [],
    fromDate: "",
    toDate: "",
    currentPage: 1,
    itemsPerPage: ITEMS_PER_PAGE,
    serviceNames: [AI_SERVICE_NAME.MEDICAL_AUDIT_NOTE]
  });

  // CS-1706 - Start - Load active button selection from session storage
  useEffect(() => {
    const saved = sessionStorage.getItem("activeActionButtonId");
    if (saved) {
      setActiveButtonId(saved);
    }
  }, []);
  // CS-1706 - End

  // Fetch practice list on mount
  useEffect(() => {
    dispatch(getPracticeListforFilter());
    //CS-1706 - preload user list 
    dispatch(fetchUsersByUserInput({ searchQuery: "", skipLoader: true }))
  }, [dispatch]);

  // Practice dropdown options
  const practiceList = useMemo(() => {
    let options: { label: string; value: any }[] = [];
    if (practiceByUserRole) {
      if (practiceByUserRole?.length > 1) {
        practiceByUserRole.forEach((item) => {
          options.push({ label: item.practiceName, value: item.practiceId });
        });
      } else if (practiceByUserRole?.length === 1) {
        setValue("practiceIds", [Number(practiceByUserRole[0]?.practiceId)]);
        practiceByUserRole.forEach((item) => {
          options.push({ label: item.practiceName, value: item.practiceId });
        });
      }
    }
    return options;
  }, [practiceByUserRole, setValue]);

  // CS-1706 - Start - Build "Uploaded By" dropdown options from fetchUsersByQuery (admin only)
  const uploadedByUserList = useMemo(() => {
    if (!isAdminUser || !fetchUsersByQuery) return [];
    return fetchUsersByQuery.map((user) => ({
      label: `${user.firstName} ${user.lastName} (${user.email})`.trim(),
      value: user.userId,
    }));
  }, [fetchUsersByQuery, isAdminUser]);

  // CS-1706 - End

  const searchAuditList = useCallback(
    (currentPage: number, obj?: SearchAiAudit) => {
      let data: SearchAiAuditInfo = {
        patientName: obj?.patientName || "",
        practiceIds: obj?.practiceIds || [],
        fromDate: obj?.fromDate || "",
        toDate: obj?.toDate || "",
        currentPage: currentPage,
        downloadState: false,
        itemsPerPage: ITEMS_PER_PAGE,
        // CS-1706 - Pass uploadedByUserIds for admin filter
        uploadedByUserIds: obj?.uploadedByUserIds || [],
        serviceName: AI_SERVICE_NAME.MEDICAL_AUDIT_NOTE,
        ...aiAuditListSort.pick(obj),
      };
      setValue("currentPage", currentPage);
      setSearchCriteria(data);
      
      // API call to fetch audit list
      if (userInfo.userRole !== USER_ROLES.ROLE_PRACTICE_USER_PRU) {
        dispatch(fetchAiAuditList(data));
      }
    },
    [setValue, dispatch]
  );

  useEffect(() => {
    setShowCard(true);
    // Load initial data
    searchAuditList(1);
  }, [searchAuditList]);

  useEffect(() => {
    if (location.state && location.state.aiAuditListSearchCriteria) {
      let searchCriteria: SearchAiAudit = location.state.aiAuditListSearchCriteria;
      setSearchCriteria(searchCriteria);
      setValue("patientName", searchCriteria?.patientName || "");
      setValue("practiceIds", searchCriteria?.practiceIds || []);
      setValue("fromDate", searchCriteria?.fromDate || "");
      setValue("toDate", searchCriteria?.toDate || "");
      if (searchCriteria.fromDate && searchCriteria.toDate) {
        setValue("dateRange", `${searchCriteria.fromDate} - ${searchCriteria.toDate}`);
      }
      // CS-1706 - Start - Restore uploadedByUserIds and serviceNames from location state
      if (isAdminUser) {
        setValue("uploadedByUserIds", searchCriteria?.uploadedByUserIds || []);
      }
      setValue("serviceNames", searchCriteria?.serviceNames || []);
      // CS-1706 - End
      searchAuditList(searchCriteria?.currentPage, searchCriteria);
    }
  }, [location.state, searchAuditList, setValue, isAdminUser]);

  useEffect(() => {
    return () => {
      dispatch(resetAiAuditList());
    };
  }, [dispatch]);

  const onSubmit = (data: SearchAiAudit) => {
    if (!isDateRangeChanged) {
      setIsClearDateRange(true);
    }
    const submitObj: SearchAiAudit = {
      patientName: data?.patientName || "",
      practiceIds: data?.practiceIds || [],
      fromDate: isDateRangeChanged ? (getValues("fromDate") || "") : "",
      toDate: isDateRangeChanged ? (getValues("toDate") || "") : "",
      currentPage: 1,
      itemsPerPage: ITEMS_PER_PAGE,
      // CS-1706 - Start - Include uploadedByUserIds and serviceNames in search
      uploadedByUserIds: isAdminUser ? (data?.uploadedByUserIds || []) : [],
      serviceNames: [AI_SERVICE_NAME.MEDICAL_AUDIT_NOTE],
      // CS-1706 - End
      ...aiAuditListSort.pick(searchCriteria),
    };
    
    navigate("/ai_medical_audit", { state: { aiAuditListSearchCriteria: submitObj } });
  };

  const handlePagination = (pageNumber: number) => {
    const data = {
      ...searchCriteria,
      currentPage: pageNumber + 1
    };
    setSearchCriteria(data);
    navigate("/ai_medical_audit", { state: { aiAuditListSearchCriteria: data } });
  };

  const handleTableSort = (columnId: string, order: "asc" | "desc") => {
    const data = aiAuditListSort.withSort(searchCriteria, columnId, order);
    if (!data) {
      return;
    }
    setSearchCriteria(data);
    navigate("/ai_medical_audit", { state: { aiAuditListSearchCriteria: data } });
  };

  const handleDateRangeChange = (fromDate: DateObject | null, toDate: DateObject | null) => {
    const fromDateString = fromDate ? fromDate.format("MM/DD/YYYY") : null;
    const toDateString = toDate ? toDate.format("MM/DD/YYYY") : null;
    setValue("fromDate", fromDateString ? fromDateString : "");
    setValue("toDate", toDateString ? toDateString : "");
    setIsDateRangeChanged(true);
  };

  const handleViewAudit = useCallback(async (auditId: string) => {
    try {
      // Dispatch the thunk and unwrap the payload
      const payload = await dispatch(getRecentAiAuditDetailsByIdData(auditId)).unwrap();
      if (payload) {
        const auditData = payload.responseJson || payload;
        
        // Handle failed audit status by showing the error from DB
        if (auditData?.status === "failed") {
          setCommentData({
            title: "Audit Failed",
            body: auditData.error || "Failed to process the audit. Please check the data and try again."
          });
          setShowCommentPopup(true);
          return;
        }

        setSelectedAuditData(payload);
        setShowAuditPopup(true);
      }
    } catch (error) {
      console.error("Error fetching audit details:", error);
      // TODO: Show error alert
    }
  }, [dispatch]);

  // CS-1706 - Start - Handle action button click with selection storage
  const handleActionButtonClick = useCallback((buttonId: string, originalClick: () => void) => {
    const currentPageKey = "aiAuditList";
    saveSelectionForPage(currentPageKey, buttonId);
    setActiveButtonId(buttonId);
    sessionStorage.setItem("activeActionButtonId", buttonId);
    originalClick();
  }, []);
  // CS-1706 - End

  // CS-1706 - Start - Handle row click to navigate to details
  const handleRowNavigateToDetails = useCallback((row: Row) => {
    const auditRow = row as AiAuditInfo;
    handleActionButtonClick(`aiAuditListActionButton-${auditRow.aiAuditDataId}`, () => {
      navigate('/ai_audit_details', {
        state: { auditId: auditRow.auditId }
      });
    });
  }, [handleActionButtonClick, navigate]);
  // CS-1706 - End

  const handleCloseAuditPopup = () => {
    setShowAuditPopup(false);
    setSelectedAuditData(null);
  };

  const handleCloseUploadPopup = () => {
    setShowUploadPopup(false);
    // Refresh the audit list after upload
    searchAuditList(searchCriteria.currentPage, searchCriteria);
  };

  // CS-1706 START - Handle Start Audit button click
  const handleStartAuditClick = () => {
    // Fetch agreement status first
    dispatch(fetchAiServiceAgreementStatus(AI_SERVICE_NAME.MEDICAL_AUDIT_NOTE)).then((res) => {
      if (res.meta && res.meta.requestStatus === "fulfilled") {
        const status = res.payload?.userSubscriptionStatus;
        // If agreement is not signed, show the agreement info popup
        if (status === null || status !== "SIGNED") {
          setShowAgreementInfoPopup(true);
        } else {
          // If agreement is signed, show the upload popup
          setShowUploadPopup(true);
        }
      }
    }).catch((err) => {
      console.log(err);
    });
  };

  const handleCloseAgreementInfoPopup = () => {
    setShowAgreementInfoPopup(false);
    dispatch(resetServiceAgreementStatus());
  };

  const handleSignAgreement = () => {
    setShowAgreementInfoPopup(false);
    setShowAgreementDialog(true);
  };

  const handleAgreementSigned = () => {
    setShowAgreementDialog(false);
    // Optionally show upload popup after signing
    setShowUploadPopup(true);
  };

  const handleAgreementCancel = () => {
    setShowAgreementDialog(false);
  };
  // CS-1706 END

  const columns: Column[] = [
    { id: "auditIdLink", label: "Audit ID"},
    { id: "patientName", label: "Patient Name" },
    { id: "practiceName", label: "Practice Name" },
    { id: "fileName", label: "File Name" },
    { id: "createdByUserName", label: "Uploaded By" },
    { id: "createdAt", label: "Upload Date" },
    { id: "currentCompliancePercentage", label: "Compliance %" },
  ];

  const formattedAuditRows = useMemo(() => {
    let formatRows: any[] = [];
    aiAuditList?.searchAuditData?.content?.forEach((row) => {
      let formatRow = {
        ...row,
        patientName: row.patientName || "-",
        practiceName: row.practiceName || "-",
        // CS-1706 - Use auditIdLink for display while preserving original auditId for uniqueValue
        auditIdLink: row.auditId ? (
          <Link
            component="button"
            underline="hover"
            variant="body2"
            onClick={(e) => {
              e.stopPropagation();
              handleActionButtonClick(`aiAuditListActionButton-${row.auditId}`, () => handleRowNavigateToDetails(row));
            }}
            color={'#00B8D9'}
          >
            {row.auditId}
          </Link>
        ) : (
          row.auditId
        ),
        currentCompliancePercentage: (
          <Typography
            sx={{
              color: getComplianceColor(row.currentCompliancePercentage),
              fontWeight: 600
            }}
          >
            {row.currentCompliancePercentage}%
          </Typography>
        ),
      };
      formatRows.push(formatRow);
    });
    return formatRows;
  }, [aiAuditList, handleActionButtonClick, handleRowNavigateToDetails]);

  const auditBorderColor = isDark ? "rgba(148,163,184,0.2)" : "rgba(15,23,42,0.12)";
  const auditPanelBg = isDark ? "#141720" : "rgba(255,255,255,0.82)";
  const auditHoverBg = isDark ? "rgba(20,184,166,0.09)" : "rgba(20,184,166,0.06)";

  const auditCardSx = {
    borderRadius: "14px",
    border: `1px solid ${auditBorderColor}`,
    backgroundColor: auditPanelBg,
    backgroundImage: "none",
    backdropFilter: "blur(8px)",
    boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.28)" : "0 12px 28px rgba(15,23,42,0.08)",
  };

  const auditFilterPanelSx = {
    ...auditCardSx,
    p: { xs: "14px !important", md: "18px !important" },
    position: "relative",
    zIndex: 20,
    overflow: "visible",
    "& .MuiAutocomplete-root, & .MuiFormControl-root": {
      width: "100%",
    },
    "& .MuiTextField-root": {
      height: "42px",
      minHeight: "42px !important",
    },
    "& .MuiOutlinedInput-root, & .MuiInputBase-root, & .MuiAutocomplete-inputRoot, & .MuiAutocomplete-root .MuiOutlinedInput-root": {
      minHeight: "42px !important",
      height: "42px !important",
      borderRadius: "10px !important",
      backgroundColor: `${modalTokens.inputBg} !important`,
      color: modalTokens.textPrimary,
      fontSize: "0.88rem",
      boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
      alignItems: "center",
      boxSizing: "border-box",
    },
    "& .MuiAutocomplete-root .MuiOutlinedInput-root": {
      padding: "0 38px 0 12px !important",
    },
    "& .MuiAutocomplete-root .MuiInputBase-input, & .MuiAutocomplete-input, & .MuiOutlinedInput-input": {
      paddingTop: "8px !important",
      paddingBottom: "8px !important",
      height: "24px !important",
      lineHeight: "24px !important",
      boxSizing: "border-box",
    },
    "& .MuiOutlinedInput-input": {
      paddingLeft: "12px !important",
      paddingRight: "12px !important",
    },
    "& .MuiAutocomplete-endAdornment": {
      top: "50% !important",
      transform: "translateY(-50%)",
      right: "8px !important",
    },
    "& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root fieldset": {
      borderColor: `${modalTokens.inputBorder} !important`,
    },
    "& .MuiOutlinedInput-root:hover fieldset, & .MuiOutlinedInput-root.Mui-focused fieldset": {
      borderColor: `${modalTokens.accent} !important`,
      borderWidth: "1px !important",
    },
    "& .MuiOutlinedInput-root.Mui-focused": {
      boxShadow: isDark ? "none" : "0 0 0 3px rgba(20,184,166,0.12)",
    },
    "& .MuiInputBase-input": {
      color: modalTokens.textPrimary,
      fontSize: "0.88rem",
    },
    "& input::selection": {
      color: modalTokens.textPrimary,
      backgroundColor: isDark ? "rgba(20,184,166,0.28)" : "rgba(20,184,166,0.2)",
    },
    "& .MuiOutlinedInput-root:has(input:-webkit-autofill)": {
      backgroundColor: "#376c9f !important",
      "& fieldset": {
        borderColor: "#7fb1df !important",
      },
    },
    "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus": {
      WebkitTextFillColor: "#ffffff",
      caretColor: "#ffffff",
      WebkitBoxShadow: "0 0 0 100px #376c9f inset",
      borderRadius: "10px",
      transition: "background-color 5000s ease-in-out 0s",
    },
    "& .MuiInputLabel-root": { color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)", fontSize: "0.88rem" },
    "& .MuiInputLabel-root.Mui-focused": { color: `${modalTokens.accent} !important` },
    "& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)": { transform: "translate(14px, 9px) scale(1)" },
    "& .MuiChip-root": {
      borderRadius: "999px",
      backgroundColor: isDark ? "rgba(20,184,166,0.16)" : "rgba(20,184,166,0.1)",
      color: modalTokens.accent,
      border: `1px solid ${isDark ? "rgba(20,184,166,0.26)" : "rgba(20,184,166,0.18)"}`,
      fontWeight: 600,
    },
    "& .MuiSvgIcon-root": {
      color: modalTokens.textSecondary,
    },
    "& .rmdp-container": {
      width: "100%",
      height: "42px",
      overflow: "visible",
      zIndex: 30,
    },
    "& .rmdp-wrapper, & .rmdp-calendar, & .rmdp-day-picker": {
      height: "auto !important",
      minHeight: "unset !important",
      overflow: "visible",
    },
    "& .rmdp-wrapper": {
      zIndex: "1600 !important",
      position: "absolute",
    },
  };

  const auditFilterFieldLabelSx = {
    fontSize: "0.8rem",
    fontWeight: 600,
    color: modalTokens.textSecondary,
    mb: 0.7,
    height: "16px",
    lineHeight: "16px",
  };

  const auditPrimaryBtnSx = {
    ...primaryBtnSx,
    color: "#fff",
    "& .MuiTypography-root": {
      color: "#fff",
      fontWeight: 700,
    },
    "& .MuiButton-startIcon": {
      color: "#fff",
    },
  };

  const auditFilterToggleBtnSx = {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "10px",
    py: 1.05,
    px: 1.6,
    color: "#00B8D9",
    borderColor: "rgba(0,184,217,0.55)",
    backgroundColor: isDark ? "rgba(0,184,217,0.08)" : "rgba(0,184,217,0.04)",
    whiteSpace: "nowrap",
    "&:hover": {
      color: "#fff",
      borderColor: "#00B8D9",
      backgroundColor: "#00B8D9",
      boxShadow: "0 6px 22px rgba(0,184,217,0.28)",
    },
  };

  const auditTableSx = {
    "& .MuiTableContainer-root": {
      borderRadius: "12px",
      border: "none",
      backgroundColor: "transparent",
    },
    "& table": {
      
      borderCollapse: "collapse",
      borderSpacing: 0,
    },
    "& .MuiTableCell-root": {
      borderBottom: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`,
      py: 1.15,
      fontSize: "0.84rem",
    },
    "& .MuiTableHead-root .MuiTableCell-root": {
      height: "42px",
    },
    "& .MuiTableHead-root .MuiTypography-root": {
      color: "#00B8D9 !important",
      fontSize: "0.78rem",
      fontWeight: 800,
      letterSpacing: "0.02em",
    },
    "& .MuiTableBody-root .MuiTableRow-root": {
      transition: "background-color 0.18s ease, box-shadow 0.18s ease",
    },
    "& .MuiTableBody-root .MuiTableRow-root:hover": {
      backgroundColor: `${auditHoverBg} !important`,
    },
    "& .MuiTableBody-root .MuiTableCell-root": {
      color: modalTokens.textPrimary,
      fontSize: "0.84rem",
    },
    "& .MuiIconButton-root": {
      color: isDark ? "#94a3b8" : "#475569",
      borderRadius: "8px",
      "&:hover": {
        color: modalTokens.accent,
        backgroundColor: isDark ? "rgba(20,184,166,0.12)" : "rgba(20,184,166,0.08)",
      },
    },
    "& .MuiPaginationItem-root": {
      borderColor: auditBorderColor,
      color: modalTokens.textSecondary,
      borderRadius: "8px",
      "&.Mui-selected": {
        color: "#fff",
        backgroundColor: modalTokens.accent,
        borderColor: modalTokens.accent,
      },
    },
  };

  return (
    <>
      <Box sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        px: { xs: 1, md: 3 },
        py: 2,
      }}>
        <Box sx={{ position: "relative", zIndex: 20, overflow: "visible" }}>
          <Collapse in={showCard} ref={filterPanelRef} onEntering={handleFilterPanelEntering} onEntered={handleFilterPanelEntered} onExited={handleFilterPanelExited}>
            <Card sx={auditFilterPanelSx}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} sm={3}>
                    <CustomTextField
                      controllerName="patientName"
                      label="Patient Name"
                      placeholder="Enter patient name"
                      control={control}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <CustomMultipleDropDown
                      name="practiceIds"
                      label="Practice Name"
                      placeholderText="Select practice"
                      control={control}
                      options={practiceList}
                      disabled={practiceList?.length === 1}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <CustomDateRange
                      label="Upload Date Range"
                      control={control}
                      controllerName="dateRange"
                      onChange={handleDateRangeChange}
                      onClear={isClearDateRange}
                    />
                  </Grid>
                  {/* CS-1706 - Start - Uploaded By filter (admin only) */}
                  {isAdminUser && (
                    <Grid item xs={12} sm={3}>
                      <CustomMultipleDropDown
                        name="uploadedByUserIds"
                        label="Uploaded By"
                        placeholderText="Select user"
                        control={control}
                        options={uploadedByUserList}
                        size="small"
                      />
                    </Grid>
                  )}
                  {/* CS-1706 - End */}
                  <Grid item md={2} xs={12}>
                    <Button
                      fullWidth
                      size="large"
                      startIcon={<SearchOutlined />}
                      sx={{ ...auditPrimaryBtnSx, height: "42px", py: 0 }}
                      type="submit"
                      removeUpperCase
                    >
                      Search
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Card>
          </Collapse>
        </Box>
        <Box mt={2} sx={{ position: "relative", zIndex: 1 }}>
          <Card sx={{ ...auditCardSx, p: { xs: "14px !important", md: "18px !important" }, position: "relative", zIndex: 1 }}>
            <Grid container justifyContent="space-between" alignItems="center" spacing={1.2}>
              <Grid item xs={12} md>
                <Typography variant='h3' fontWeight={800} color="text.primary">AI Medical Audit List</Typography>
              </Grid>
              {/* CS-1706 START - Show only Start Audit button with purple color */}
              {hasSubscribedAccessToMedicalAudit && (
                <Grid item xs={12} md="auto">
                  <Button
                    variant="contained"
                    onClick={handleStartAuditClick}
                    startIcon={<Brain size={18} />}
                    fullWidth
                    removeUpperCase
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.86rem",
                      borderRadius: "10px",
                      py: 1.05,
                      px: 1.6,
                      background: "linear-gradient(135deg, #6366f1, #8b5cf6, #a855f7)",
                      color: "#ffffff !important",
                      "& .MuiTypography-root": {
                        color: "#ffffff !important",
                      },
                      "&:hover": {
                        background: "linear-gradient(135deg, #5558e3, #7c3aed, #9333ea)",
                        color: "#ffffff !important",
                        boxShadow: "0 6px 22px rgba(99,102,241,0.35)",
                        "& .MuiTypography-root": {
                          color: "#ffffff !important",
                        },
                      }
                    }}
                  >
                    Start Audit
                  </Button>
                </Grid>
              )}
              {/* CS-1706 END */}
              <Grid item xs={12} md="auto">
                <Button
                  variant="outlined"
                  color="info"
                  onClick={toggleFilterPanel}
                  startIcon={<SearchOutlined />}
                  fullWidth
                  sx={auditFilterToggleBtnSx}
                  removeUpperCase
                >
                  {showCard ? "Hide Filters" : "Show Filters"}
                </Button>
              </Grid>
            </Grid>
            <Table
              columns={columns}
              rows={formattedAuditRows || []}
              pageNumber={handlePagination}
              totalPages={aiAuditList?.searchAuditData?.totalPages}
              totalElementText={"Records: " + (aiAuditList?.searchAuditData?.totalElements ? aiAuditList?.searchAuditData?.totalElements : "0")}
              emptyText="No Audit Records Found"
              currentPageNumber={getValues("currentPage") ? getValues("currentPage") - 1 : 0}
              showMenu={false}
              sortableColumns={aiAuditListSort.sortableColumns}
              serverSideSorting
              onSortChange={handleTableSort}
              controlledSortColumn={aiAuditListSort.controlledColumn(searchCriteria.sortBy)}
              controlledSortOrder={searchCriteria.sortOrder ?? null}
              uniqueValue="aiAuditDataId"
              currentPageKey="aiAuditList"
              activeButtonId={activeButtonId}
              onRowClick={handleRowNavigateToDetails}
              clickableCards={true}
              sx={auditTableSx}
            />
          </Card>
        </Box>
      </Box>

      {/* Medical Note Audit Popup */}
      <MedicalNoteAuditPopup
        open={showAuditPopup}
        onClose={handleCloseAuditPopup}
        initialAuditData={selectedAuditData}
        auditCompleted={true}
      />

      {/* Medical Note Audit Upload Popup */}
      <MedicalNoteAuditUploadPopup
        open={showUploadPopup}
        onClose={handleCloseUploadPopup}
      />

      {/* CS-1706 START - Agreement Info Popup */}
      <Dialog
        open={showAgreementInfoPopup}
        onClose={handleCloseAgreementInfoPopup}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 4 }}>
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold", mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
              AI Medical Note Audit
            </Typography>

            <Typography variant="body1" paragraph>
              Our AI-powered Medical Note Audit tool helps healthcare professionals ensure the completeness, accuracy, and compliance of medical documentation.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: "bold" }}>
              Key Features:
            </Typography>
            <Typography variant="body1" component="div">
              <ul>
                <li>Automated analysis of medical notes for completeness</li>
                <li>Identification of missing or incomplete information</li>
                <li>Compliance checking against documentation standards</li>
                <li>Suggestions for improving documentation quality</li>
                <li>HIPAA-compliant processing and secure data handling</li>
              </ul>
            </Typography>

            <Box sx={{ mt: 4, p: 2, bgcolor: "rgba(124, 58, 237, 0.1)", borderRadius: 2 }}>
              <Typography variant="body2" paragraph sx={{ mb: 2 }}>
                To use this feature, you must first review and sign the AI Audit Agreement which outlines the terms, limitations, and your responsibilities when using this tool.
              </Typography>
              <Button
                variant="contained"
                startIcon={<DriveFileRenameOutline />}
                onClick={handleSignAgreement}
                sx={{
                  background: "linear-gradient(135deg, #4f46e5, #7c3aed, #9333ea)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #4338ca, #6d28d9, #7e22ce)",
                  }
                }}
              >
                Sign Agreement
              </Button>
            </Box>

            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
              <Button onClick={handleCloseAgreementInfoPopup} color="error">Close</Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Agreement Dialog */}
      <AiAuditAgreement
        open={showAgreementDialog}
        onAgreementSigned={handleAgreementSigned}
        onCancel={handleAgreementCancel}
        userServiceAgreementStatus={serviceAgreementStatus}
      />
      {/* CS-1706 END */}
      <CustomCommentPopup
        open={showCommentPopup}
        onClose={() => setShowCommentPopup(false)}
        title={commentData.title}
        commentBody={commentData.body}
      />
    </>
  );
};

export default AiAuditList;
